import classNames from 'classnames';
import { useState } from 'react';
import React from 'react';
import Icon from '@/components/atoms/icon/icon';
import Image from '@/components/atoms/image/image';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import RoomMatrixRoomBeds from '../room-matrix-room-beds/room-matrix-room-beds';
import RoomMatrixRoomDetails from '../room-matrix-room-details/room-matrix-room-details';
import styles from './room-matrix-room.module.scss';

export interface RoomMatrixRoomProps {
  /** The number of adult beds */
  adultBeds: number;
  /** The number of children beds */
  childrenBeds: number;
  /** The class name to apply */
  className?: string;
  /** The description of the room */
  description: string | null;
  /** The number of extra beds */
  extraBeds: number;
  /** The facilities in the room */
  facilitiesInRoom: string[];
  /** Whether to display the loading state */
  isLoading?: boolean;
  /** The name of the room */
  name: string;
  /** The number of persons */
  personsNo: number;
  /** The images of the room */
  pictures: Content.Properties.HotelPicture[];
  /** Indicates whether is a single room */
  singleRoom?: boolean;
}

export const ROOM_PLACEHOLDER_IMAGE_URL = '/assets/missing-image.png';

const RoomMatrixRoom = ({
  adultBeds = 0,
  childrenBeds = 0,
  className,
  description,
  extraBeds = 0,
  facilitiesInRoom = [],
  isLoading = false,
  name,
  personsNo,
  pictures = [],
  singleRoom = false,
}: RoomMatrixRoomProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const thumbnailImage =
    pictures.find(picture => picture.isMain) || pictures[0] || {};

  if (isLoading) {
    return (
      <div
        className={classNames(styles.component, className, styles.isLoading, {
          [styles.hasSingleRoom]: singleRoom,
        })}
      >
        <SkeletonLoader
          className={`${styles.loadingImage} ${styles.loader}`}
          height="var(--roomMatrix-loadingImageHeight)"
          width="var(--roomMatrix-loadingImageWidth)"
        />
        <div className={styles.loadingDetails}>
          <SkeletonLoader className={styles.loader} height="20px" width="95%" />
          <SkeletonLoader className={styles.loader} height="16px" width="80%" />
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div
        className={classNames(styles.component, className, {
          [styles.hasSingleRoom]: singleRoom,
        })}
        onClick={() => setIsOpen(true)}
        onKeyDown={() => setIsOpen(true)}
        role="button"
        tabIndex={-1}
      >
        <Image
          alt={thumbnailImage.description || ''}
          aspectRatio="sixteenByNine"
          className={styles.image}
          height={47}
          src={thumbnailImage.url || ROOM_PLACEHOLDER_IMAGE_URL}
          srcSet={{
            laptop: {
              height: singleRoom ? 111 : 47,
              width: singleRoom ? 196 : 83,
            },
          }}
          unoptimized
          width={83}
        />

        <div className={styles.details}>
          <div className={styles.name}>
            <Icon className={styles.icon} name="ArrowRight" size={12} />

            <Typography tagStyle="bodyMedium">{name}</Typography>
          </div>

          <RoomMatrixRoomBeds
            adultBeds={adultBeds}
            childrenBeds={childrenBeds}
            extraBeds={extraBeds}
            personsNo={personsNo}
          />
        </div>
      </div>

      <RoomMatrixRoomDetails
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        room={{
          adultBeds,
          childrenBeds,
          description,
          extraBeds,
          facilitiesInRoom,
          name,
          personsNo,
          pictures,
        }}
      />
    </React.Fragment>
  );
};

RoomMatrixRoom.displayName = 'RoomMatrixRoom';

export default RoomMatrixRoom;
